/* eslint-disable @typescript-eslint/prefer-for-of */
/**
 * QA Tools Parameter Parser
 *
 * QA-specific parser for JSON/XML report parameters with path normalization
 * that maps to UI section names matching the Report Edit UI.
 *
 * IMPORTANT: This file is only used by QA Tools and does not affect
 * any other part of the application.
 */

import {
  FIELD_MAPPINGS,
  getOptionDisplayName,
  getSectionForOption,
  SectionName,
  SINGLE_VALUE_OPTIONS,
} from './qa-field-mappings';

export interface QaParameterEntry {
  path: string; // Normalized path for comparison
  originalPath: string; // Original path from source data
  value: any; // The actual value
  type: string; // "string" | "number" | "boolean" | "date" | "array" | "object"
  section: SectionName; // Section matching Report Edit UI
  displayName: string; // Human-readable name for display
}

export class QaParameterParser {
  /**
   * Parse JSON string and flatten to parameter entries
   */
  static parseJson(json: string): QaParameterEntry[] {
    try {
      const obj = JSON.parse(json);
      return this.flattenObject(obj, '', '');
    } catch {
      return [];
    }
  }

  /**
   * Parse XML string and flatten to parameter entries
   */
  static parseXml(xml: string): QaParameterEntry[] {
    try {
      const jsonObj = this.xmlToJson(xml);
      return this.flattenObject(jsonObj, '', '');
    } catch {
      return [];
    }
  }

  /**
   * Flatten nested object/array to parameter entries
   */
  private static flattenObject(
    obj: any,
    prefix: string,
    originalPrefix: string,
  ): QaParameterEntry[] {
    const entries: QaParameterEntry[] = [];

    if (obj === null || obj === undefined) {
      return entries;
    }

    // Handle arrays
    if (Array.isArray(obj)) {
      // Check if this is an array of options (all items have optionName/optionSequence/optionValue)
      const isOptionsArray =
        obj.length > 0 &&
        obj.every((item) => {
          if (typeof item !== 'object' || !item) return false;
          const keys = Object.keys(item).map((k) => k.toLowerCase());
          return (
            keys.includes('optionname') &&
            keys.includes('optionsequence') &&
            keys.includes('optionvalue')
          );
        });

      if (isOptionsArray) {
        return this.processOptionsArray(obj, prefix);
      }

      // Regular array handling for non-option arrays
      obj.forEach((item, index) => {
        const newPrefix = `${prefix}[${index}]`;
        entries.push(...this.flattenObject(item, newPrefix, newPrefix));
      });
      return entries;
    }

    // Handle objects
    if (typeof obj === 'object') {
      // Check if this is a notification object
      const notificationEntry = this.tryProcessNotification(obj, prefix);
      if (notificationEntry) {
        entries.push(notificationEntry);
        return entries;
      }

      // Check if this is a filter object
      const filterEntry = this.tryProcessFilter(obj, prefix);
      if (filterEntry) {
        entries.push(filterEntry);
        return entries;
      }

      // Check if this is an option object
      const optionEntry = this.tryProcessOption(obj, prefix);
      if (optionEntry) {
        entries.push(optionEntry);
        return entries;
      }

      // Regular object processing
      Object.keys(obj).forEach((key) => {
        const newPrefix = prefix ? `${prefix}.${key}` : key;
        const value = obj[key];

        // If value is primitive, add as entry
        if (this.isPrimitive(value)) {
          // Skip null, undefined, and empty string values
          if (value !== null && value !== undefined && value !== '') {
            const normalizedPath = this.normalizePath(newPrefix);
            const section = this.determineSectionFromPath(normalizedPath);
            const displayName = this.getDisplayNameFromPath(normalizedPath);

            entries.push({
              path: normalizedPath,
              originalPath: newPrefix,
              value: value,
              type: this.getType(value),
              section: section,
              displayName: displayName,
            });
          }
        } else {
          // Recursively flatten
          entries.push(...this.flattenObject(value, newPrefix, newPrefix));
        }
      });
      return entries;
    }

    // Primitive value at root (shouldn't happen normally)
    if (prefix) {
      const normalizedPath = this.normalizePath(prefix);
      const section = this.determineSectionFromPath(normalizedPath);
      const displayName = this.getDisplayNameFromPath(normalizedPath);

      entries.push({
        path: normalizedPath,
        originalPath: prefix,
        value: obj,
        type: this.getType(obj),
        section: section,
        displayName: displayName,
      });
    }

    return entries;
  }

  /**
   * Process an array of option objects (with optionName/optionSequence/optionValue)
   */
  private static processOptionsArray(
    options: any[],
    prefix: string,
  ): QaParameterEntry[] {
    const entries: QaParameterEntry[] = [];

    // Separate sort/break options, custom fields, and other options
    const sortBreakOptions: any[] = [];
    const customFieldOptions: any[] = [];
    const otherOptions: any[] = [];

    options.forEach((item) => {
      const nameKey = Object.keys(item).find(
        (k) => k.toLowerCase() === 'optionname',
      );
      const name = nameKey ? String(item[nameKey]).toLowerCase() : '';

      if (
        name === 'sort_column' ||
        name === 'sort_order' ||
        name === 'break' ||
        name === 'page_break'
      ) {
        sortBreakOptions.push(item);
      } else if (name === 'custom_field') {
        customFieldOptions.push(item);
      } else {
        otherOptions.push(item);
      }
    });

    // Process sort/break options grouped by sequence
    if (sortBreakOptions.length > 0) {
      const sequenceGroups = new Map<string, any[]>();

      sortBreakOptions.forEach((item) => {
        const seqKey = Object.keys(item).find(
          (k) => k.toLowerCase() === 'optionsequence',
        );
        const seq = seqKey ? String(item[seqKey]) : '0';

        if (!sequenceGroups.has(seq)) {
          sequenceGroups.set(seq, []);
        }
        sequenceGroups.get(seq)!.push(item);
      });

      // Create one entry per sequence group
      sequenceGroups.forEach((group, seq) => {
        const parts: string[] = [];

        group.forEach((item) => {
          const nameKey = Object.keys(item).find(
            (k) => k.toLowerCase() === 'optionname',
          );
          const valKey = Object.keys(item).find(
            (k) => k.toLowerCase() === 'optionvalue',
          );

          const name = nameKey ? item[nameKey] : '';
          const val = valKey ? item[valKey] : '';
          parts.push(`${name}:${val}`);
        });

        entries.push({
          path: `sortbreak.sequence.${seq}`,
          originalPath: `${prefix}[sortbreak.${seq}]`,
          value: parts.join('|'),
          type: 'string',
          section: 'Sort fields',
          displayName: `Sort/Break #${seq}`,
        });
      });
    }

    // Process custom field options individually by sequence
    if (customFieldOptions.length > 0) {
      customFieldOptions.forEach((item) => {
        const seqKey = Object.keys(item).find(
          (k) => k.toLowerCase() === 'optionsequence',
        );
        const valKey = Object.keys(item).find(
          (k) => k.toLowerCase() === 'optionvalue',
        );

        const seq = seqKey ? String(item[seqKey]) : '0';
        const val = valKey ? item[valKey] : '';

        entries.push({
          path: `customfield.sequence.${seq}`,
          originalPath: `${prefix}[customfield.${seq}]`,
          value: val,
          type: 'string',
          section: 'Report fields',
          displayName: `Custom Field #${seq}`,
        });
      });
    }

    // Process other options individually
    otherOptions.forEach((item) => {
      const entry = this.tryProcessOption(item, prefix);
      if (entry) {
        entries.push(entry);
      }
    });

    return entries;
  }

  /**
   * Try to process an object as a notification (email/ownerEmail/suppressEmail)
   */
  private static tryProcessNotification(
    obj: any,
    prefix: string,
  ): QaParameterEntry | null {
    const emailKey = Object.keys(obj).find((k) => k.toLowerCase() === 'email');
    const ownerEmailKey = Object.keys(obj).find(
      (k) => k.toLowerCase() === 'owneremail',
    );
    const suppressEmailKey = Object.keys(obj).find(
      (k) => k.toLowerCase() === 'suppressemail',
    );

    // If this object has email and either ownerEmail or suppressEmail, it's a notification
    if (emailKey && (ownerEmailKey || suppressEmailKey)) {
      const email = obj[emailKey] ?? '';
      const ownerEmail = ownerEmailKey ? (obj[ownerEmailKey] ?? '') : '';
      const suppressEmail = suppressEmailKey
        ? (obj[suppressEmailKey] ?? '')
        : '';

      const safeEmail =
        email === null || email === undefined
          ? ''
          : String(email).trim().toLowerCase();

      const canonicalPath = safeEmail
        ? `notifications.notification.${safeEmail}`
        : 'notifications.notification';

      return {
        path: canonicalPath,
        originalPath: prefix,
        value: `email:${email}|owner:${ownerEmail}|suppress:${suppressEmail}`,
        type: 'string',
        section: 'Email Section',
        displayName: safeEmail || 'Notification',
      };
    }

    return null;
  }

  /**
   * Try to process an object as a filter (tableAlias/columnName/operator/expression)
   */
  private static tryProcessFilter(
    obj: any,
    prefix: string,
  ): QaParameterEntry | null {
    const tableAliasKey = Object.keys(obj).find(
      (k) => k.toLowerCase() === 'tablealias',
    );
    const columnNameKey = Object.keys(obj).find(
      (k) => k.toLowerCase() === 'columnname',
    );
    const operatorKey = Object.keys(obj).find(
      (k) => k.toLowerCase() === 'operator',
    );

    if (!tableAliasKey || !columnNameKey || !operatorKey) return null;

    const tableAlias = String(obj[tableAliasKey] ?? '')
      .trim()
      .toLowerCase();
    const columnName = String(obj[columnNameKey] ?? '')
      .trim()
      .toLowerCase();
    const operator = String(obj[operatorKey] ?? '').trim();
    const expressionKey = Object.keys(obj).find(
      (k) => k.toLowerCase() === 'expression',
    );
    const expression = expressionKey
      ? String(obj[expressionKey] ?? '').trim()
      : '';

    const canonicalPath = `filter.${tableAlias}.${columnName}`;
    const value = expression ? `${operator}:${expression}` : operator;
    const displayName = columnName
      .split('_')
      .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ');

    return {
      path: canonicalPath,
      originalPath: prefix,
      value,
      type: 'string',
      section: 'Filter detail',
      displayName,
    };
  }

  /**
   * Try to process an object as an option (optionName/optionSequence/optionValue)
   */
  private static tryProcessOption(
    obj: any,
    prefix: string,
  ): QaParameterEntry | null {
    const optionNameKey = Object.keys(obj).find(
      (k) => k.toLowerCase() === 'optionname',
    );

    if (!optionNameKey) {
      return null;
    }

    const rawName = obj[optionNameKey];
    const safeName =
      rawName === null || rawName === undefined
        ? ''
        : String(rawName).trim().toLowerCase();

    const optionSeqKey = Object.keys(obj).find(
      (k) => k.toLowerCase() === 'optionsequence',
    );
    const optionValKey = Object.keys(obj).find(
      (k) => k.toLowerCase() === 'optionvalue',
    );

    const optionSeq =
      optionSeqKey && obj[optionSeqKey] !== undefined ? obj[optionSeqKey] : '';
    const optionVal =
      optionValKey && obj[optionValKey] !== undefined ? obj[optionValKey] : '';

    const section = getSectionForOption(safeName);
    const displayName = getOptionDisplayName(safeName);

    // Check if this is a single-value option (should have one row, not multiple)
    const isSingleValue = SINGLE_VALUE_OPTIONS.has(safeName);

    let canonicalPath: string;

    if (isSingleValue) {
      // Single-value options: use just the option name (no value/sequence suffix)
      // This ensures only one row per option
      canonicalPath = safeName
        ? `outputs.option.${safeName}`
        : 'outputs.option';
    } else {
      // Multi-value options: use value in path so same values match regardless of order
      // e.g., outputs.option.include_these_payment_types.voids
      const safeVal = this.normalizeValueForPath(optionVal);
      canonicalPath = safeName
        ? safeVal
          ? `outputs.option.${safeName}.${safeVal}`
          : `outputs.option.${safeName}.${optionSeq}`
        : 'outputs.option';
    }

    // Display just the option name, value is shown in the value columns
    const displayLabel = displayName;

    return {
      path: canonicalPath,
      originalPath: prefix,
      value: optionVal,
      type: 'string',
      section: section,
      displayName: displayLabel,
    };
  }

  /**
   * Normalize path using field mappings
   */
  private static normalizePath(path: string): string {
    const lowerPath = path.toLowerCase();

    // Check field mappings for normalization
    for (const mapping of FIELD_MAPPINGS) {
      for (const pattern of mapping.patterns) {
        if (pattern.test(lowerPath)) {
          return mapping.canonical;
        }
      }
    }

    // Treat report IDs under Outputs/Output as the same field
    if (lowerPath.includes('output') && lowerPath.includes('reportid')) {
      return 'outputs.output.reportid';
    }

    return path;
  }

  /**
   * Determine section from path using field mappings
   */
  private static determineSectionFromPath(path: string): SectionName {
    const lowerPath = path.toLowerCase();

    // Check field mappings first
    for (const mapping of FIELD_MAPPINGS) {
      for (const pattern of mapping.patterns) {
        if (pattern.test(lowerPath)) {
          return mapping.section;
        }
      }
    }

    // Additional pattern matching for paths not in field mappings
    const inNotifications = lowerPath.includes('notification');
    const isQuickFilter =
      lowerPath.includes('quickfilter') ||
      lowerPath.includes('quick_filter') ||
      lowerPath.includes('quick filter');

    // Report info patterns
    if (
      lowerPath.includes('system') ||
      lowerPath.includes('model') ||
      lowerPath.includes('sourceapp') ||
      (!inNotifications &&
        (lowerPath.includes('owner') ||
          lowerPath.includes('requestid') ||
          lowerPath.includes('reportid') ||
          lowerPath.includes('requestname') ||
          lowerPath.includes('signature') ||
          lowerPath.includes('template') ||
          lowerPath.includes('reportmasterid') ||
          isQuickFilter))
    ) {
      return 'Report info';
    }

    // Customize email patterns
    if (
      lowerPath.includes('customemail') ||
      lowerPath.includes('custom_email')
    ) {
      return 'Customize email';
    }

    // Report schedule patterns
    if (
      lowerPath.includes('schedule') ||
      lowerPath.includes('frequency') ||
      lowerPath.includes('rundate') ||
      lowerPath.includes('recurrence') ||
      lowerPath.includes('datetorun') ||
      lowerPath.includes('priority')
    ) {
      return 'Report schedule';
    }

    // Email Section patterns
    if (
      lowerPath.includes('recipient') ||
      lowerPath.includes('email') ||
      lowerPath.includes('notification')
    ) {
      return 'Email Section';
    }

    // Filter detail patterns
    if (
      lowerPath.includes('criteria') ||
      lowerPath.includes('filter') ||
      lowerPath.includes('prompt')
    ) {
      return 'Filter detail';
    }

    // Report options patterns (output format, delivery, etc.)
    if (
      lowerPath.includes('output') ||
      lowerPath.includes('format') ||
      lowerPath.includes('delivery') ||
      lowerPath.includes('destination')
    ) {
      return 'Report options';
    }

    return 'Other';
  }

  /**
   * Normalize a value for use in canonical path
   * For dates, converts to YYYY_MM_DD format so 01-01-2026 and 1-1-2026 create same path
   */
  private static normalizeValueForPath(value: any): string {
    if (value === null || value === undefined) {
      return '';
    }

    const strVal = String(value).trim();
    if (!strVal) {
      return '';
    }

    // Try to normalize as a date first
    // Check for MM-DD-YYYY or M-D-YYYY format (with dashes)
    const usDashMatch = strVal.match(/^(\d{1,2})-(\d{1,2})-(\d{4})$/);
    if (usDashMatch) {
      const month = usDashMatch[1].padStart(2, '0');
      const day = usDashMatch[2].padStart(2, '0');
      const year = usDashMatch[3];
      return `${year}_${month}_${day}`;
    }

    // Check for MM/DD/YYYY or M/D/YYYY format (with slashes)
    const usSlashMatch = strVal.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (usSlashMatch) {
      const month = usSlashMatch[1].padStart(2, '0');
      const day = usSlashMatch[2].padStart(2, '0');
      const year = usSlashMatch[3];
      return `${year}_${month}_${day}`;
    }

    // Check for YYYY-MM-DD format (ISO)
    const isoMatch = strVal.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
    if (isoMatch) {
      const year = isoMatch[1];
      const month = isoMatch[2].padStart(2, '0');
      const day = isoMatch[3].padStart(2, '0');
      return `${year}_${month}_${day}`;
    }

    // Not a date - just normalize as string (lowercase, replace special chars with underscore)
    return strVal.toLowerCase().replace(/[^a-z0-9_]/g, '_');
  }

  /**
   * Get display name from path
   */
  private static getDisplayNameFromPath(path: string): string {
    const lowerPath = path.toLowerCase();

    // Check field mappings for display name
    for (const mapping of FIELD_MAPPINGS) {
      for (const pattern of mapping.patterns) {
        if (pattern.test(lowerPath)) {
          if (mapping.displayName) {
            return mapping.displayName;
          }
          break;
        }
      }
    }

    // Extract the last segment and format it
    const segments = path.split('.');
    const lastSegment = segments[segments.length - 1];

    // Remove array indices
    const cleanSegment = lastSegment.replace(/\[\d+\]/g, '');

    // Convert camelCase or PascalCase to Title Case with spaces
    return cleanSegment
      .replace(/([a-z])([A-Z])/g, '$1 $2')
      .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
      .replace(/_/g, ' ')
      .split(' ')
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  /**
   * Convert XML string to JSON object
   */
  private static xmlToJson(xmlString: string): any {
    const parser = new DOMParser();
    const xmlDoc = parser.parseFromString(xmlString, 'text/xml');

    // Check for parsing errors
    const parserError = xmlDoc.querySelector('parsererror');
    if (parserError) {
      throw new Error('XML parsing error');
    }

    return this.xmlNodeToJson(xmlDoc.documentElement);
  }

  /**
   * Recursively convert XML node to JSON
   */
  private static xmlNodeToJson(node: Element): any {
    const obj: any = {};

    // Add attributes with @ prefix
    if (node.attributes.length > 0) {
      for (let i = 0; i < node.attributes.length; i++) {
        const attr = node.attributes[i];
        obj[`@${attr.name}`] = attr.value;
      }
    }

    // Handle child nodes
    if (node.childNodes.length > 0) {
      const children: Record<string, any[]> = {};

      for (let i = 0; i < node.childNodes.length; i++) {
        const child = node.childNodes[i];

        // Skip text nodes that are just whitespace
        if (child.nodeType === Node.TEXT_NODE) {
          const text = child.textContent?.trim();
          if (text) {
            return this.convertValue(text);
          }
          continue;
        }

        // Process element nodes
        if (child.nodeType === Node.ELEMENT_NODE) {
          const childElement = child as Element;
          const childName = childElement.nodeName;

          if (!children[childName]) {
            children[childName] = [];
          }
          children[childName].push(this.xmlNodeToJson(childElement));
        }
      }

      // Convert to object or array
      Object.keys(children).forEach((key) => {
        if (children[key].length === 1) {
          obj[key] = children[key][0];
        } else {
          obj[key] = children[key];
        }
      });
    }

    return obj;
  }

  /**
   * Check if value is primitive
   */
  private static isPrimitive(value: any): boolean {
    return (
      value === null ||
      value === undefined ||
      typeof value === 'string' ||
      typeof value === 'number' ||
      typeof value === 'boolean'
    );
  }

  /**
   * Get type of value
   */
  private static getType(value: any): string {
    if (value === null || value === undefined) {
      return 'string';
    }

    if (typeof value === 'boolean') {
      return 'boolean';
    }

    if (typeof value === 'number') {
      return 'number';
    }

    if (typeof value === 'string') {
      // Try to detect date
      if (this.isDateString(value)) {
        return 'date';
      }
      return 'string';
    }

    if (Array.isArray(value)) {
      return 'array';
    }

    if (typeof value === 'object') {
      return 'object';
    }

    return 'string';
  }

  /**
   * Check if string looks like a date
   */
  private static isDateString(value: string): boolean {
    // Simple date detection patterns
    const datePatterns = [
      /^\d{4}-\d{2}-\d{2}/, // YYYY-MM-DD
      /^\d{2}\/\d{2}\/\d{4}/, // MM/DD/YYYY
      /^\d{2}-\d{2}-\d{4}/, // DD-MM-YYYY
    ];

    return datePatterns.some((pattern) => pattern.test(value));
  }

  /**
   * Convert string value to appropriate type
   */
  private static convertValue(value: string): any {
    // Try boolean
    if (value === 'true') return true;
    if (value === 'false') return false;

    // Try number
    if (!isNaN(Number(value)) && value.trim() !== '') {
      return Number(value);
    }

    return value;
  }
}
